import { useCallback, useEffect, useRef, useState } from 'react';
import { openAuthenticatedSse } from '@api/sse';
import { useSseStream, type SseEvent } from '@hooks/useSseStream';
import { refineAppRunUrl, startAppRunUrl } from '@api/apps';
import type {
  AppRunDetail,
  RefineAppRunRequest,
  StartAppRunRequest,
} from '@api/apps';
import type { AskUserQuestion } from '@hooks/useChat';
import { parseToolApprovalRequest, type ToolApprovalRequest } from '@utils/toolApprovalRequest';

/**
 * What the browser learns from the stream while a turn is in flight.
 *
 * <p>None of it is authoritative — the run row is, and it is refetched whenever
 * a turn ends. This is the live view laid over it, so a dropped event costs a
 * moment of staleness rather than a wrong run state.
 */
export interface AppRunLiveState {
  isStreaming: boolean;
  /** Latest step the agent reported this turn, or null before it reports one. */
  liveStepId: string | null;
  /** One line saying what is happening right now. */
  activity: string | null;
  /** Questions the agent asked during this turn. */
  liveQuestions: readonly AskUserQuestion[] | null;
  /** Actions on the person's own computer waiting for their OK. */
  pendingApprovals: readonly ToolApprovalRequest[];
  /** A turn-level failure worth a banner. Never replaces the run's own state. */
  streamError: string | null;
  /**
   * The `turnStartedAt` of the run as it stood when the last stream ended, so a
   * row still showing that turn as RUNNING is known to be stale.
   */
  endedTurn: string | null;
}

interface UseAppRunStreamOptions {
  run: AppRunDetail | null;
  /**
   * Called once the server has accepted a turn. The row is RUNNING from that
   * moment, and a caller still holding the DRAFT it read before would otherwise
   * not learn so until the turn ends.
   */
  onTurnStart?: () => void;
  /** Called when a turn ends, so the caller can refetch the authoritative row. */
  onTurnEnd: () => void;
  /**
   * Called when something the run already shows has gained a rendering the row
   * carries — a 3D view derived from an uploaded CAD input. Mid-turn, so the
   * refetch that ends the turn would be far too late to be the one that shows it.
   */
  onInputPreview?: () => void;
  /** Called when the server has named the run's conversation from its first instruction. */
  onTitle?: (conversationId: string, title: string) => void;
}

export const IDLE_LIVE: AppRunLiveState = {
  isStreaming: false,
  liveStepId: null,
  activity: null,
  liveQuestions: null,
  pendingApprovals: [],
  streamError: null,
  endedTurn: null,
};

const withoutApproval = (
  approvals: readonly ToolApprovalRequest[],
  toolUseId: unknown,
): readonly ToolApprovalRequest[] => approvals.filter((approval) => approval.toolUseId !== toolUseId);

/** Events that ask for, or settle, an action waiting on the person's OK. */
const APPROVAL_EVENTS = new Set(['tool_approval_request', 'tool_start', 'tool_executing', 'tool_rejected', 'tool_complete']);

const approvalsAfter = ({ type, data }: SseEvent, approvals: readonly ToolApprovalRequest[]) => {
  if (type !== 'tool_approval_request') return withoutApproval(approvals, data.toolUseId);
  const request = parseToolApprovalRequest(data);
  // A reconnect replays requests still pending, so the same one can arrive twice.
  return request ? [...withoutApproval(approvals, request.toolUseId), request] : approvals;
};

/** Tools whose names would read as noise on a progress line. */
const SILENT_TOOLS = new Set([
  'load_skill',
  'acquire_environment',
  'release_environment',
  'report_app_step',
  'ask_user',
]);

const humanizeTool = (tool: string): string =>
  tool.replaceAll('_', ' ').replace(/^./, (c) => c.toUpperCase());

const parseQuestions = (raw: unknown): AskUserQuestion[] | null => {
  if (typeof raw !== 'string' || !raw.trim()) return null;
  try {
    const parsed: unknown = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as AskUserQuestion[]) : null;
  } catch {
    return null;
  }
};

/** A turn the server refused, with the status that says why. */
export class StreamRequestError extends Error {
  readonly status: number;

  constructor(status: number, message: string) {
    super(message);
    this.name = 'StreamRequestError';
    this.status = status;
  }
}

const errorMessageFor = async (response: Response): Promise<string> => {
  try {
    const body: unknown = await response.json();
    const message = (body as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  } catch {
    // A body that isn't JSON tells us nothing the status doesn't.
  }
  return response.status === 409
    ? 'This app is already working on your request.'
    : 'The app could not be started. Try again.';
};

/**
 * Drives one app run's turns.
 *
 * <p>Starting, refining and answering are each a POST that replies with the same
 * SSE stream a chat turn does, so this reads it with the shared parser and keeps
 * only what the run screen shows. Attaching to a turn already in flight is the
 * conversation's own reconnect endpoint: a run has no separate live turn of its
 * own to reconnect to.
 */
export const useAppRunStream = ({
  run,
  onTurnStart,
  onTurnEnd,
  onInputPreview,
  onTitle,
}: UseAppRunStreamOptions) => {
  const [live, setLive] = useState<AppRunLiveState>(IDLE_LIVE);
  const abortRef = useRef<AbortController | null>(null);
  /** Bumped per stream opened, so a superseded one can tell it has been. */
  const generationRef = useRef(0);
  const onTurnStartRef = useRef(onTurnStart);
  onTurnStartRef.current = onTurnStart;
  const onTurnEndRef = useRef(onTurnEnd);
  onTurnEndRef.current = onTurnEnd;
  const onInputPreviewRef = useRef(onInputPreview);
  onInputPreviewRef.current = onInputPreview;
  const onTitleRef = useRef(onTitle);
  onTitleRef.current = onTitle;
  const runRef = useRef(run);
  runRef.current = run;

  const handleEvent = useCallback((event: SseEvent) => {
    const { type, data } = event;
    if (APPROVAL_EVENTS.has(type)) {
      setLive((s) => ({ ...s, pendingApprovals: approvalsAfter(event, s.pendingApprovals) }));
    }
    switch (type) {
      case 'app_step':
        if (typeof data.stepId === 'string') {
          setLive((s) => ({ ...s, liveStepId: data.stepId as string, activity: null }));
        }
        return;
      case 'tool_start':
      case 'tool_executing': {
        const tool = typeof data.tool === 'string' ? data.tool : null;
        if (!tool || SILENT_TOOLS.has(tool)) return;
        const summary = typeof data.summary === 'string' ? data.summary : null;
        setLive((s) => ({ ...s, activity: summary ?? humanizeTool(tool) }));
        return;
      }
      case 'app_input_preview':
        onInputPreviewRef.current?.();
        return;
      case 'conversation_title': {
        const { conversationId, title } = data;
        if (typeof conversationId === 'string' && typeof title === 'string') {
          onTitleRef.current?.(conversationId, title);
        }
        return;
      }
      case 'ask_user': {
        const questions = parseQuestions(data.questions);
        if (questions?.length) {
          setLive((s) => ({ ...s, liveQuestions: questions, activity: null }));
        }
        return;
      }
      case 'done':
        // The turn is over the moment the server says so. Waiting for the
        // connection to drop instead leaves the screen working for up to the
        // stale-read timeout, long enough for the user to act on a stale
        // screen and have the next turn refused as a concurrent one.
        abortRef.current?.abort();
        abortRef.current = null;
        setLive((s) => ({
          ...s,
          isStreaming: false,
          activity: null,
          pendingApprovals: [],
          endedTurn: runRef.current?.turnStartedAt ?? null,
        }));
        return;
      case 'error':
        setLive((s) => ({
          ...s,
          streamError:
            typeof data.error === 'string' ? data.error : 'The app stopped unexpectedly.',
        }));
        return;
      case 'insufficient_credits':
        setLive((s) => ({
          ...s,
          streamError: 'You ran out of credits part-way through this run.',
        }));
        return;
      case 'guardrail_violation':
        setLive((s) => ({
          ...s,
          streamError: 'This request was blocked by a content policy.',
        }));
        return;
      default:
        return;
    }
  }, []);

  const { readStream, abort } = useSseStream(handleEvent);

  const stop = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    abort();
  }, [abort]);

  useEffect(() => stop, [stop]);

  // The screen stays mounted from one run to the next, so a stream the last run
  // left open must not be read as this run working.
  const runId = run?.runId ?? null;
  const [prevRunId, setPrevRunId] = useState(runId);
  if (prevRunId !== runId) {
    setPrevRunId(runId);
    setLive(IDLE_LIVE);
  }
  useEffect(() => {
    // Retired before it is aborted, so its wind-down neither reports a turn
    // ending nor refetches the run now on screen.
    generationRef.current += 1;
    stop();
  }, [runId, stop]);

  /**
   * Read one turn to its end. A stream that drops is not a failure the user has
   * to act on: the turn carries on server-side, and the refetch that follows
   * shows where it actually got to.
   */
  const consume = useCallback(
    async (response: Response, generation: number) => {
      if (generation !== generationRef.current) return;
      if (!response.body) {
        throw new Error('The server did not open a stream for this run.');
      }
      try {
        await readStream(response.body.getReader());
      } catch {
        // Nothing to report: the run row decides what happened.
      } finally {
        // Aborting a reader does not settle it before the replacement opens, so
        // a superseded stream reaches here with a newer one already live. It has
        // to stay silent: clearing abortRef would leave the new turn with
        // nothing able to stop it, and standing the screen down mid-turn would
        // put it back to "working" only at the next event.
        // Only a done event ends the turn: a connection that merely dropped
        // leaves it running server-side, and the screen must still be able to
        // reconnect to it.
        if (generation === generationRef.current) {
          abortRef.current = null;
          setLive((s) => ({ ...s, isStreaming: false, activity: null, pendingApprovals: [] }));
          onTurnEndRef.current();
        }
      }
    },
    [readStream],
  );

  const post = useCallback(
    async (path: string, body: unknown) => {
      stop();
      const generation = ++generationRef.current;
      const controller = new AbortController();
      abortRef.current = controller;
      setLive({ ...IDLE_LIVE, isStreaming: true });

      const response = await openAuthenticatedSse(path, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
        signal: controller.signal,
      });
      if (!response.ok) {
        if (generation === generationRef.current) {
          abortRef.current = null;
          setLive(IDLE_LIVE);
        }
        throw new StreamRequestError(response.status, await errorMessageFor(response));
      }
      if (generation === generationRef.current) {
        onTurnStartRef.current?.();
      }
      // Settled once the server has taken the turn; the turn itself is read in
      // the background, and the row says how it ends.
      consume(response, generation).catch(() => undefined);
    },
    [consume, stop],
  );

  const start = useCallback(
    (request: StartAppRunRequest) =>
      run ? post(startAppRunUrl(run.runId), request) : Promise.resolve(),
    [post, run],
  );

  const refine = useCallback(
    (request: RefineAppRunRequest) =>
      run ? post(refineAppRunUrl(run.runId), request) : Promise.resolve(),
    [post, run],
  );

  /**
   * Attach to a turn that was already running when this screen mounted, which is
   * what makes a reload or a second tab show live progress instead of a frozen
   * snapshot.
   */
  const attach = useCallback(
    async (conversationId: string) => {
      // Overwriting the controller without this would strand whatever it was
      // holding: the request stays open with nothing left able to cancel it.
      stop();
      const generation = ++generationRef.current;
      const controller = new AbortController();
      abortRef.current = controller;
      setLive({ ...IDLE_LIVE, isStreaming: true });
      const response = await openAuthenticatedSse(
        `/api/v1/conversations/${encodeURIComponent(conversationId)}/reconnect`,
        { method: 'GET', signal: controller.signal },
      );
      if (!response.ok) {
        if (generation !== generationRef.current) return;
        abortRef.current = null;
        setLive(IDLE_LIVE);
        onTurnEndRef.current();
        return;
      }
      await consume(response, generation);
    },
    [consume, stop],
  );

  const clearStreamError = useCallback(
    () => setLive((s) => ({ ...s, streamError: null })),
    [],
  );

  /** Settled by the user; the tool's next event would say so too, but a dropped one must not leave it asking. */
  const settleApproval = useCallback(
    (toolUseId: string) => setLive((s) => ({ ...s, pendingApprovals: withoutApproval(s.pendingApprovals, toolUseId) })),
    [],
  );

  return { live, start, refine, attach, stop, clearStreamError, settleApproval };
};
